import React, { useRef, useState } from "react";
import styled from "styled-components";
import apiClient from "../utils/apiClient";
import DataTable from "../components/DataTable";
import dayjs from "dayjs";
import { Button, Chip } from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import LinearProgressWithLabel from "./LinearProgressWithLabel";
import Box from "@mui/material/Box";
import FileSaver from "file-saver";

const ButtonsBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  gap: 10px;
`;

const FilesSection = ({ agentId }) => {
  const { t } = useTranslation();

  const user = useSelector((state) => state.user.user);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const fileInputRef = useRef(null);

  const isAdmin =
    user && user.privileges && user.privileges.includes("AGENTADMIN");

  const handleRefresh = () => {
    setRefresh((prev) => prev + 1);
  };

  const handleChooseFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("agent_id", agentId);

    try {
      await apiClient.post("/documents", formData, {
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setUploadProgress(percent);
        },
      });

      alert(t("createAgent.files.fileUploaded"));
      handleRefresh();
    } catch (err) {
      console.error(t("createAgent.files.uploadErrorLog"), err);
      alert(t("createAgent.files.uploadError"));
    } finally {
      e.target.value = null;
      setUploadProgress(0);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (window.confirm(t("createAgent.files.confirmDeleteFile"))) {
      try {
        await apiClient.delete(`/documents/${fileId}?agent_id=${agentId}`);
        alert(t("createAgent.files.fileDeleted"));
        handleRefresh();
      } catch (err) {
        console.error(t("createAgent.files.deleteErrorLog"), err);
        alert(t("createAgent.files.deleteError"));
      }
    }
  };

  const handleToggleRequiresSignature = async (doc) => {
    const newValue = doc.requires_signature === 1 ? "false" : "true";

    try {
      await apiClient.post(`/documents/${doc.id}/update-signature`, {
        requires_signature: newValue,
      });
      alert(t("createAgent.files.signatureUpdated"));
      handleRefresh();
    } catch (err) {
      console.error(t("createAgent.files.signatureUpdateErrorLog"), err);
      alert(t("createAgent.files.signatureUpdateError"));
    }
  };

  const downloadDocument = async (document) => {
    try {
      const response = await apiClient.get(
        `/documents/download/${document.id}?agent_id=${agentId}`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data]);
      FileSaver.saveAs(blob, document.original_name);
    } catch (error) {
      console.error(t("createAgent.files.downloadErrorLog"), error);
    }
  };

  const filesColumns = [
    {
      field: "user_id",
      headerName: t("createAgent.files.columns.uploadedBy"),
      flex: 1,
      renderCell: (params) =>
        params.row.user_id === user.id
          ? t("createAgent.files.uploadedByYou")
          : t("createAgent.files.uploadedByAdmin"),
    },
    {
      field: "uploaded_at",
      headerName: t("createAgent.files.columns.uploadedAt"),
      flex: 1,
      valueFormatter: (value) => {
        return dayjs(value).format("DD.MM.YYYY HH:mm:ss");
      },
    },
    {
      field: "original_name",
      headerName: t("createAgent.files.columns.name"),
      flex: 2,
    },
    {
      field: "signStatus",
      headerName: t("createAgent.files.columns.status"),
      flex: 1,
      renderCell: (params) => {
        if (params.row.is_signed) {
          return (
            <Chip
              size="small"
              variant="outlined"
              color="success"
              label={t("createAgent.files.status.signed")}
            />
          );
        }

        if (params.row.requires_signature) {
          return (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={t("createAgent.files.status.waitingSignature")}
            />
          );
        }

        return <span></span>;
      },
    },
    {
      field: "action",
      headerName: t("createAgent.files.columns.actions"),
      width: isAdmin ? 300 : 180,
      renderCell: (params) => (
        <ButtonsBlock>
          <Button
            size="small"
            variant="contained"
            color="primary"
            type="button"
            onClick={() => downloadDocument(params.row)}
          >
            {t("createAgent.files.download")}
          </Button>

          <Button
            size="small"
            variant="outlined"
            color="error"
            type="button"
            onClick={() => handleDeleteFile(params.row.id)}
          >
            {t("createAgent.files.delete")}
          </Button>

          {isAdmin && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              type="button"
              onClick={() => handleToggleRequiresSignature(params.row)}
            >
              {params.row.requires_signature === 1
                ? t("createAgent.files.disableSignature")
                : t("createAgent.files.enableSignature")}
            </Button>
          )}
        </ButtonsBlock>
      ),
    },
  ];

  return (
    <div>
      <ButtonsBlock>
        <Button
          variant="contained"
          color="primary"
          onClick={handleChooseFile}
          fullWidth
          startIcon={<AddIcon />}
        >
          {t("createAgent.files.addFile")}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={handleRefresh}
          fullWidth
        >
          {t("createAgent.files.refresh")}
        </Button>
      </ButtonsBlock>

      <input
        style={{ display: "none" }}
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
      />

      {uploadProgress > 0 && (
        <Box sx={{ width: "50%", mb: 2 }}>
          <LinearProgressWithLabel value={uploadProgress} color="primary" />
        </Box>
      )}

      <DataTable
        path="/documents"
        queryParams={{ agent_id: agentId, type: "files_agents" }}
        refresh={refresh}
        columns={filesColumns}
      />
    </div>
  );
};

export default FilesSection;
